<template>
    <div>
        <NotificationAlert
            v-if="showError"
            :subtitle="$t('r_love.video.issue')"
            :close-button="false"
        />
        <div class="h-0 relative pt-[56.25%] mt-6">
            <video
                :class="[!isReady ? 'visible' : 'hidden']"
                class="absolute top-0 left-0 w-full h-full bg-black"
                id="preview"
                width="100%"
                height="100%"
                autoplay
                muted
            ></video>

            <video
                :class="[isReady ? 'visible' : 'hidden']"
                class="absolute top-0 left-0 w-full h-full bg-black"
                id="recording"
                width="100%"
                height="100%"
                controls
            ></video>
        </div>
        <div class="flex flex-col mt-4 space-y-5">
            <ButtonSecondaryMd
                v-if="!isRecording && !isReady"
                id="startButton"
                @click="onClickStart"
            >
                <CircleIcon class="ct-text-error" />
                <span>
                    {{ $t("r_shared.video.start") }}
                </span>
            </ButtonSecondaryMd>

            <ButtonSecondaryMd v-if="isRecording" id="stopButton" @click="onClickStop">
                {{ $t("r_shared.video.end") }}
            </ButtonSecondaryMd>

            <ButtonSecondaryMd v-if="isReady" @click="onClickAgain">
                <ArrowReloadIcon />
                <span>{{ $t("r_shared.video.record_again") }}</span>
            </ButtonSecondaryMd>
            <ButtonSecondaryMd v-if="isReady" @click="onClickDownload">
                <AddIcon />
                <span>{{ $t("r_shared.video.add") }}</span>
            </ButtonSecondaryMd>
        </div>
        <span id="log"></span>
    </div>
</template>

<script>
import { handleActionClick } from "@/shared/utils";
import { EventBus } from "@/shared/projects/event-bus.ts";
import { mapMutations } from "vuex";
import { ButtonSecondaryMd } from "@easy/ui/buttons";
import { CircleIcon, ArrowReloadIcon, AddIcon } from "@easy/ui/icons";
import { NotificationAlert } from "@easy/ui/notifications";
export default {
    name: "cVideoMaker",
    components: {
        ButtonSecondaryMd,
        CircleIcon,
        AddIcon,
        ArrowReloadIcon,
        NotificationAlert,
    },
    emits: ["loadVideo"],
    props: { previewMode: Boolean },
    data() {
        return {
            showError: false,
            recordingReady: false,
            recordingTimeMS: 60000,
            file: String,
            isRecording: false,
            isReady: false,
            stream: null,
        };
    },
    mounted() {
        this.preview = document.getElementById("preview");
        this.recording = document.getElementById("recording");
        this.startButton = document.getElementById("startButton");
        this.stopButton = document.getElementById("stopButton");
        this.logElement = document.getElementById("log");
        this.initiateRecorder();

        EventBus.$on("checkVideoStatus", () => {
            EventBus.$emit("videoStatusChecked", this.isReady);
            if (this.isReady) {
                this.$emit("loadVideo", this.file);
            }
        });
    },
    beforeUnmount() {
        this.onClickStop();
        EventBus.$off("checkVideoStatus");
    },
    methods: {
        ...mapMutations(["showAlert"]),
        log(msg) {
            this.logElement.innerText += msg + "\n";
        },
        wait(delayInMS) {
            return new Promise((resolve) => setTimeout(resolve, delayInMS));
        },
        initiateRecorder() {
            navigator.mediaDevices
                .getUserMedia({
                    audio: true,
                    video: true,
                })
                .then((stream) => {
                    this.stream = stream;
                    if ("srcObject" in this.preview) {
                        this.preview.srcObject = stream;
                    } else {
                        this.preview.src = window.URL.createObjectURL(stream);
                    }

                    return new Promise((resolve) => (this.preview.onplaying = resolve));
                })
                .catch(() => {
                    this.showError = true;
                });
        },
        async startRecording(stream, lengthInMS) {
            const recorder = new MediaRecorder(stream);
            const data = [];

            recorder.ondataavailable = (event) => data.push(event.data);
            recorder.start();

            const stopped = new Promise((resolve, reject) => {
                recorder.onstop = resolve;
                recorder.onerror = (event) => reject(event.name);
            });

            const recorded = new Promise((resolve) => {
                setTimeout(resolve, lengthInMS);
                EventBus.$on("stopRecording", resolve);
            }).then(() => recorder.state === "recording" && recorder.stop());

            await Promise.all([stopped, recorded]);
            return data;
        },
        stop(stream) {
            stream.getTracks().forEach((track) => track.stop());
            this.isRecording = false;
        },
        onClickStart() {
            if (this.previewMode) {
                handleActionClick();
                return;
            }
            this.isRecording = true;
            this.startRecording(this.stream, this.recordingTimeMS)
                .then((recordedChunks) => {
                    this.stop(this.preview.srcObject);
                    const recordedBlob = new Blob(recordedChunks, {
                        type: "video/webm",
                    });

                    this.recording.src = URL.createObjectURL(recordedBlob);

                    this.file = new File([recordedBlob], "RecordedVideo.webm");
                    this.isReady = true;
                })
                .catch((error) => {
                    if (error.name === "NotFoundError") {
                        this.log("Camera or microphone not found. Can't record.");
                        this.showError = true;
                    } else {
                        this.log(error);
                        this.showError = true;
                    }
                });
        },
        onClickStop() {
            EventBus.$emit("stopRecording");
            this.stop(this.preview.srcObject);
        },
        onClickDownload() {
            if (!this.file.size) {
                this.showError = true;
            } else {
                this.$emit("loadVideo", this.file);
            }
        },
        onClickAgain() {
            this.isReady = false;
            this.initiateRecorder();
        },
    },
};
</script>
